import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { checkoutErrors, CheckoutStatus, Features, PaymentMethodType, TerminalErrorCode, TopazStatus } from '../../model'
import { getPrefetchRoutes, Route, usePostLoginRoute } from '../../config/router'
import { Cohort } from '@afterpaytouch/portal-api'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCohort,
  useConsumerFacilityCohort,
  useConsumerSelector,
  useCountry,
  useCreditFacilityRedirect,
  useEffectiveRoutingDecision,
  usePreferredPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  usePreferredBankAccount,
  usePreferredCard,
  useRecoverableTerminalErrorsState,
  useRoutingDecision,
  useRoutingDecisionReason,
  useShouldShowCashAppTooltip,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useShouldUseBankAccount,
  useIsBankAccountSelectionFailed,
  useTopazState,
  useSavedCards,
  handlePreferredFallback,
  updatePreferredCardWithError,
  updateSavedCardsWithError,
  useStartCheckout,
  setIsCreditCardDisabled,
  setCheckoutError,
  useIsAUPayMonthlyOrderLimiteTerminal,
  setLoggedInLoadingMessageKey,
  useActiveCashAppPay,
  useOfferDetailPBI,
  useIsEligibleForAddPaymentMethodFlowExperiment,
  useIsControlForAddPaymentMethodFlowExperiment,
  useAddPaymentMethodFlowExperimentTrigger,
  useIsInlinePaymentOptions,
  useHoldRoutingForLdReady,
  useShouldShowWelcomeWithInitialLimit,
  accountLookup,
  useRequiresAccountRefresh,
  setRequiresAccountRefresh,
  useReadyFeatureFlag,
} from '../../state'
import { creditCheckService } from '@afterpaytouch/portal-api/consumer/creditCheck'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { consumeUsFebConsentInteractiveLogin } from '../../flow/one-offer-page/consentLoginSignal'
import { consumeUsFebConsentCopyDisplayed } from '../../flow/one-offer-page/consentCopySignal'
import { phoneNumberIsCleared } from '../../../src/utils/phone-numbers'
import { BankAccountDetails, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { Text, useToast } from '@afterpaytouch/core'
import { useFlag } from '@afterpaytouch/feature'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { useTranslation } from '../../utils/convergence'

// Upper bound on how long the pre-/start consent POST may hold up checkout. Comfortably above the
// POST's normal latency; a session that hits it degrades to LEGACY routing rather than hanging.
const US_FEB_CONSENT_POST_TIMEOUT_MS = 5000

/**
 * CASHDAP-3449: report how the one-offer-page routing decision resolved, once per /start
 * resolution on the LoggedIn page. Keeps the raw server value (ABSENT when the field was
 * omitted, i.e. the server flag is off) distinct from what the FE actually honoured
 * (LEGACY_FALLBACK when the FE flag is off, the value was unknown, or the eligibility
 * guard dropped it).
 *
 * Gated on the same flags-ready signal as LoggedIn's navigation effect: on a refresh of
 * /logged-in the persisted store rehydrates with status already Started while LaunchDarkly
 * is still identifying the consumer, and reading the ONE_OFFER_PAGE flag in that window
 * would latch a LEGACY_FALLBACK report for a session whose routing actually honoured the
 * decision.
 */
export const useTrackOneOfferPageRouteResolved = (): void => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { status } = useCheckoutState()
  const routingDecision = useRoutingDecision()
  const routingDecisionReason = useRoutingDecisionReason()
  const effectiveRoutingDecision = useEffectiveRoutingDecision()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const hasTracked = useRef(false)

  useEffect(() => {
    if (hasTracked.current || status !== CheckoutStatus.Started || !hasFeatureFlagsReady) {
      return
    }
    hasTracked.current = true
    logEvent(TrackingEvent.ONE_OFFER_PAGE_ROUTE_RESOLVED, {
      routingDecision: routingDecision ?? 'ABSENT',
      // Raw server attribution for a LEGACY decision (CASHDAP-3457); NONE mirrors the
      // BE metric's tag for "no reason", i.e. non-LEGACY or omitted decisions.
      routingDecisionReason: routingDecisionReason ?? 'NONE',
      effectiveRoutingDecision: effectiveRoutingDecision ?? 'LEGACY_FALLBACK',
    })
  }, [status, routingDecision, routingDecisionReason, effectiveRoutingDecision, hasFeatureFlagsReady, logEvent])
}

export interface PostLoginNavigation {
  navigatePostLogin: () => void
}

/**
 * Post-login navigation shared by every routing effect on this page.
 */
export const usePostLoginNavigation = (): PostLoginNavigation => {
  const router = useAppRouter()
  const postLoginRoute = usePostLoginRoute()
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const redirectToCreditFacility = useCreditFacilityRedirect()
  const isExistingLcccUser =
    consumerFacilityCohort?.getStartedContext === 'LCCC_EXISTING' &&
    consumerFacilityCohort?.eligibility === 'eligible' &&
    !consumerFacilityCohort?.enteredAsNewUser
  // Skip the "Step 2 of 2" interstitial for existing LCCC users and
  // redirect them directly to the credit-application product page (APL-1119).
  // New users who completed onboarding this session (enteredAsNewUser) must still
  // see the interstitial before being redirected.
  // Only intercept when postLoginRoute would send them to GET_STARTED so that
  // recoverable-error routes and other priority routes are still honoured.
  const navigatePostLogin = useCallback(() => {
    if (isExistingLcccUser && postLoginRoute === Route.GET_STARTED) {
      redirectToCreditFacility()
    } else {
      router.replace(postLoginRoute)
    }
  }, [isExistingLcccUser, redirectToCreditFacility, router, postLoginRoute])

  return { navigatePostLogin }
}

/**
 * Mirrors the Apple Pay LaunchDarkly flags into the checkout store so downstream
 * pages/selectors can read them from Redux.
 */
export const useSyncApplePayFlagsToStore = (): void => {
  const dispatch = useAppDispatch()
  const isApplePayCreditCardDisabled = useFlag<boolean>(Features.APPLE_PAY_DISABLE_CREDIT_CARD_IN_CHECKOUT, false)

  useEffect(() => {
    dispatch(setIsCreditCardDisabled(isApplePayCreditCardDisabled))
  }, [isApplePayCreditCardDisabled])
}

/**
 * Kicks off `startCheckout` once feature flags are ready (refreshing the account first when
 * required), unless the consumer must add a mobile number first. Returns `requestCompleted`:
 * true once the start request has finished, or immediately when the page (re)loads with a
 * checkout that is already Started.
 */
export const useStartCheckoutOnceReady = (): boolean => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { status } = useCheckoutState()
  const { mobile, identityCheckStatus } = useConsumerSelector()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { status: recoverableFlowStatus } = useRecoverableTerminalErrorsState()
  const requiresAccountRefresh = useRequiresAccountRefresh()
  const { doStartCheckoutAndHandleError } = useStartCheckout()
  const [startRequestCompleted, setStartRequestCompleted] = useState(false)
  // Single owner for the refresh → consent → /start sequence. accountLookup's fulfilment
  // updates identityCheckStatus — a dep of the effect below — so the effect re-fires while the
  // first invocation is still awaiting the consent POST (a window the bounded await stretches
  // to seconds; pre-consent it was microtask-thin). The re-fire sees the one-shot consent
  // signals already consumed and would dispatch a duplicate /start that races the very consent
  // POST /start must follow. Dropping it is safe: the first invocation runs /start with the
  // refreshed account data already in the store.
  const startSequenceInFlight = useRef(false)
  const country = useCountry()
  // Same LD-ready read mechanism as useEffectiveRoutingDecision — live by the time the effect
  // fires (it gates on consumerFeatureFlagReady). Deliberately a separate flag from
  // ONE_OFFER_PAGE: consent capture ramps together with the backend's
  // `paylater.enable-checkout-evergreen-consent`, ahead of the one-offer page itself.
  const isEvergreenConsentEnabled = useReadyFeatureFlag<boolean>(Features.ENABLE_CHECKOUT_EVERGREEN_CONSENT, false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  /**
   * CASHDAP-3450: transmit the US FEB credit-check consent (V4/CONSUMER_LENDING) implied by the
   * login page's evergreen consent copy. Must settle BEFORE /start — the backend reads the
   * consent from the bureau during /start to compute the routing decision, so firing this in a
   * parallel effect would race it. Once per fresh interactive login (password/OTP/2FA, plus the
   * verify-flow, first-password-creation and verify-challenge completions that are deliberately
   * not remember-me mint sites — see flow/one-offer-page/consentLoginSignal.ts), never for
   * silent persistent-login resumes, which show no consent copy, and never for a login whose
   * /login mount did not actually render the copy (the flag is re-read here against the
   * identified LD context, which can disagree with the pre-login read that gated the display —
   * the display mark is the source of truth). A failed POST must not block /start: the consent
   * stays uncaptured and the routing decision degrades to LEGACY.
   */
  const postUsFebConsentIfApplicable = async (): Promise<void> => {
    if (!isEvergreenConsentEnabled || country !== Country.US || !consumeUsFebConsentInteractiveLogin() || !consumeUsFebConsentCopyDisplayed()) {
      return
    }
    const consentMutation = dispatch(
      creditCheckService.endpoints.updateConsentStatus.initiate({
        consentStatus: { creditChecks: { consented: true, version: 'V4' } },
        channel: 'CHECKOUT_LOGIN',
      })
    )
    // Both settle paths map to a value so the race can never surface a rejection — an unwrap()
    // rejection after a lost race would otherwise be an unhandled promise rejection.
    const consentRequest = consentMutation.unwrap().then(
      () => 'posted' as const,
      () => 'failed' as const
    )
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    try {
      // The portal-api transport has NO timeout (axios timeout defaults to 0), so the await must
      // be bounded here or a hung connection holds /start — and the whole checkout — hostage. On
      // timeout the POST keeps running in the background; this session just proceeds without the
      // consent and the routing decision degrades to LEGACY.
      const outcome = await Promise.race([
        consentRequest,
        new Promise<'timed-out'>((resolve) => {
          timeoutHandle = setTimeout(resolve, US_FEB_CONSENT_POST_TIMEOUT_MS, 'timed-out')
        }),
      ])
      logEvent(TrackingEvent.ONE_OFFER_PAGE_LOGIN_CONSENT_POSTED, { success: outcome === 'posted', outcome })
    } finally {
      clearTimeout(timeoutHandle)
      consentMutation.reset()
    }
  }

  useEffect(() => {
    const startCheckoutAndHandleError = async (): Promise<void> => {
      if (startSequenceInFlight.current) {
        return
      }
      startSequenceInFlight.current = true
      try {
        if (requiresAccountRefresh) {
          const result = await dispatch(accountLookup())
          if (accountLookup.fulfilled.match(result)) {
            dispatch(setRequiresAccountRefresh(false))
          }
        }
        await postUsFebConsentIfApplicable()
        await doStartCheckoutAndHandleError()
        setLoggedInLoadingMessageKey(null)
        setStartRequestCompleted(true)
      } finally {
        // Released so a later legitimate re-fire (e.g. a recoverable-error retry with status
        // still Loaded) can start again — only concurrent duplicates are dropped.
        startSequenceInFlight.current = false
      }
    }

    if (phoneNumberIsCleared(mobile)) {
      router.replace(Route.ADD_MOBILE)
    } else if (
      status === CheckoutStatus.Loaded ||
      status === CheckoutStatus.Closed ||
      status === CheckoutStatus.IdentityIneligible ||
      recoverableFlowStatus === RecoverableTerminalErrorStatus.Finished
    ) {
      if (consumerFeatureFlagReady) {
        startCheckoutAndHandleError()
      }
    }
  }, [consumerFeatureFlagReady, recoverableFlowStatus, identityCheckStatus])

  // If the logged-in page is refreshed after checkout already started, the local request
  // state resets — a Started checkout status counts as "completed" just like a finished request.
  return startRequestCompleted || status === CheckoutStatus.Started
}

export interface PaymentMethodRoutingArgs extends Pick<PostLoginNavigation, 'navigatePostLogin'> {
  requestCompleted: boolean
}

/**
 * The post-start brain of this page: once checkout has Started, pick the purchase method
 * (Cash App Pay / Apple Pay / bank account / preferred card, with fallbacks), route the
 * consumer onward, and — when Topaz card verification fails — mark the failing card and retry
 * with the next available saved card before navigating.
 *
 * Three effects cooperate through `checkAlternativePreferred` and the Redux store:
 * 1. card-fallback consumer: when a card fails verification, re-runs checks on the next valid card
 * 2. main routing: selects the purchase method and navigates once the started checkout settles
 * 3. Topaz watcher: reacts to card-verification results, triggering the fallback loop
 */
export const usePaymentMethodRouting = ({ requestCompleted, navigatePostLogin }: PaymentMethodRoutingArgs): void => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const toast = useToast()
  const { textSize } = useCheckoutSizes()
  const { status } = useCheckoutState()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()
  const isBankAccountSelectionFailed = useIsBankAccountSelectionFailed()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const preferredCard = usePreferredCard()
  const savedCards = useSavedCards()
  const cashAppPay = useActiveCashAppPay()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const topazStatus = useTopazState()
  const { shouldOverrideCashAppPay } = useShouldShowCashAppTooltip()
  const isEligibleForAddPaymentMethodFlowExperiment = useIsEligibleForAddPaymentMethodFlowExperiment()
  const isControlForAddPaymentMethodFlowExperiment = useIsControlForAddPaymentMethodFlowExperiment()
  const triggerAddPaymentMethodFlowExperiment = useAddPaymentMethodFlowExperimentTrigger()
  const shouldShowWelcomeWithInitialLimit = useShouldShowWelcomeWithInitialLimit()
  const cohort = useCohort()
  // The main routing effect below already waits for hasFeatureFlagsReady, but the topaz and
  // card-fallback effects can navigate before LD identify completes — at which point
  // usePostLoginRoute resolves against the legacy cascade and strands a decision-cohort
  // session on the wrong surface. Hold those navigations (capped) while a routing decision
  // is waiting on LD readiness.
  const holdRoutingForLdReady = useHoldRoutingForLdReady()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()
  const [checkAlternativePreferred, setCheckAlternativePreferred] = useState(false)

  useEffect(() => {
    // Both branches end in navigation — wait for LD readiness so the route honours the
    // routing decision (the effect refires via the dep when the hold clears).
    if (holdRoutingForLdReady) {
      return
    }
    // check if fallback is available
    const fallback = savedCards.filter((card) => card.error === null).pop() ?? null
    if (checkAlternativePreferred && fallback != null) {
      // do checks on next available valid card
      dispatch(handlePreferredFallback(fallback, navigatePostLogin))
    } else if (checkAlternativePreferred && fallback === null) {
      // if no available fallback, preferred card stays the same as the original and error is set before navigating to summary
      dispatch(updatePreferredCardWithError(preferredCard, savedCards))
      navigatePostLogin()
    }
  }, [checkAlternativePreferred, savedCards, holdRoutingForLdReady])

  useEffect(() => {
    // APL-1424: the Pay Monthly order-limit terminal is terminal — its dedicated effect above
    // has already dispatched the error and navigated to /oh-no in this same flush (this effect
    // runs with the committed render, whose status is still Started), so its no-method branch
    // would dispatch LackOfEligiblePaymentMethod and replace the terminal with
    // ADD_PAYMENT_METHOD, and the CashOnly branch would overwrite the Pay Monthly error with
    // the generic Default. Every navigation below that flows through navigatePostLogin already
    // resolves to TERMINAL_ERROR via usePostLoginRoute; this guard covers this effect's direct
    // router.replace calls. The shared selector is flag/population-gated, so flag-off,
    // non-target and Apple Retail sessions keep the normal payment setup.
    if (isPayMonthlyOrderLimitTerminal) {
      return
    }
    if (status === CheckoutStatus.Started && requestCompleted && hasFeatureFlagsReady && !checkAlternativePreferred) {
      if (shouldShowWelcomeWithInitialLimit) {
        router.replace(Route.WELCOME_WITH_INITIAL_LIMIT)
        return
      }

      if (cashAppPay.length > 0 && (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY || shouldOverrideCashAppPay)) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (shouldUseApplePay) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.APPLE_PAY }))
      } else if (shouldUseBankAccount) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.ACH_BANK_ACCOUNT, account: preferredBankAccount?.account as BankAccountDetails }, false))
      } else if (preferredPaymentMethod === PaymentMethodType.CREDIT_CARD && preferredCard?.card !== null) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (cashAppPay.length > 0) {
        // fallback to cashapp
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (preferredCard?.card !== null) {
        // fallback to card
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (!isConsumerLendingEligible) {
        if (cohort === Cohort.CashOnly) {
          dispatch(setCheckoutError({ error: TerminalErrorCode.Default }))
          router.replace(Route.TERMINAL_ERROR)
        } else if (isEligibleForAddPaymentMethodFlowExperiment) {
          // Fire the variant-A trigger here — gated on this effect's
          // `requestCompleted && hasFeatureFlagsReady` guard — so we only enroll users actually
          // sent down the experimental branch, not users transiently appearing eligible due to
          // mid-flow `preferredCard.card` mutations.
          triggerAddPaymentMethodFlowExperiment()
          navigatePostLogin()
        } else {
          if (isControlForAddPaymentMethodFlowExperiment) {
            // Symmetric variant-C trigger. Fires immediately before routing to the standard
            // add-payment-method page so the control arm enrolls the same no-payment-method
            // population that the experimental branch above enrolls for variant A. The trigger
            // function reads experiment.value, so it logs variant=C automatically for these
            // users without needing a variant-specific call.
            triggerAddPaymentMethodFlowExperiment()
          }
          dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
          router.replace(Route.ADD_PAYMENT_METHOD)
        }
        return
      }

      if (isBankAccountSelectionFailed) {
        toast({
          message: () => (
            <>
              <Text size={textSize} bold>
                {t('summary:bankAccountErrors:fraudCheckNotAllowed:heading', { lastFour: preferredBankAccount?.account?.numberMask })}
              </Text>
              <Text size={textSize}>{t('summary:bankAccountErrors:fraudCheckNotAllowed:message')}</Text>
            </>
          ),
          kind: 'Default',
          toastTime: 5000,
        })
      }

      if (preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD || isConsumerLendingEligible) {
        navigatePostLogin()
      }
    }
  }, [status, requestCompleted, hasFeatureFlagsReady, shouldShowWelcomeWithInitialLimit, isPayMonthlyOrderLimitTerminal])

  useEffect(() => {
    if (requestCompleted && topazStatus.purchase.status > TopazStatus.Saving && !checkAlternativePreferred) {
      if (topazStatus.purchase.status === TopazStatus.Saved || preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD) {
        // Only the navigation waits for LD readiness (refired via the dep when the hold
        // clears); the card-error bookkeeping in the else branch is routing-independent.
        if (!holdRoutingForLdReady) {
          navigatePostLogin()
        }
      } else {
        dispatch(updateSavedCardsWithError(topazStatus.purchase.status, preferredCard?.card?.token, savedCards))
        setCheckAlternativePreferred(true)
      }
    }
  }, [topazStatus.purchase.status, requestCompleted, holdRoutingForLdReady])
}

/**
 * Warms the router cache for every page the consumer can be routed to from here.
 */
export const usePrefetchPostLoginRoutes = (): void => {
  const router = useAppRouter()
  const country = useCountry()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const verifyRoutes = getPrefetchRoutes(country)

  useEffect(() => {
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.ADD_MOBILE)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.SUMMARY)
    if (isInlinePaymentOptions) {
      router.prefetch(Route.PAYMENT_OPTIONS)
    } else {
      router.prefetch(Route.PAYMENT_TYPES)
    }
    router.prefetch(Route.PAY_MONTHLY_ONLY)
    router.prefetch(Route.WELCOME_WITH_INITIAL_LIMIT)
    verifyRoutes.map((url) => router.prefetch(url))
  }, [verifyRoutes, isInlinePaymentOptions])
}

/**
 * Routes to the terminal-error page when the pay-by-installments offer was declined and the
 * consumer has no consumer-lending alternative — except the Pay Monthly order-limit terminal
 * population (APL-1424), which can match with no PCL offer and whose terminal error must win.
 *
 * Held until LaunchDarkly has identified the current consumer: before that the umbrella flag
 * defaults off, so the terminal predicate cannot distinguish "not this population" from
 * "not yet resolved", and firing early would overwrite the Pay Monthly terminal with the
 * legacy error — and the navigation unmounts this page, whose effect would correct it.
 */
export const usePBIRejectionRedirect = (): void => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const offerDetailPBI = useOfferDetailPBI()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()

  useEffect(() => {
    if (!hasFeatureFlagsReady) {
      return
    }
    if (offerDetailPBI?.rejectedReasonCode && !isConsumerLendingEligible && !isPayMonthlyOrderLimitTerminal) {
      dispatch(setCheckoutError({ error: checkoutErrors[offerDetailPBI?.rejectedReasonCode] ?? TerminalErrorCode.Default }))
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [hasFeatureFlagsReady, offerDetailPBI?.rejectedReasonCode, isConsumerLendingEligible, isPayMonthlyOrderLimitTerminal])
}

/**
 * APL-1424: sets the Pay Monthly order-limit terminal error when a successful /start resolves
 * to the terminal population (useIsAUPayMonthlyOrderLimiteTerminal). usePostLoginRoute only
 * chooses the route; this effect owns the error state — so /oh-no renders the Pay Monthly
 * guidance instead of the generic terminal — plus the navigation itself, so the outcome
 * cannot race the credit-facility redirect.
 */
export const useDispatchAUPayMonthlyOrderLimitTerminalError = (): void => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()

  useEffect(() => {
    if (isPayMonthlyOrderLimitTerminal) {
      dispatch(setCheckoutError({ error: TerminalErrorCode.PayMonthlyOrderAmountTooHigh, errorCode: ErrorCode.OrderAmountTooHigh }))
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [isPayMonthlyOrderLimitTerminal])
}
